import React, { useState, useMemo } from "react";
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Select,
  Heading,
  Button,
  Badge,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useColorModeValue,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Input,
  InputGroup,
  InputLeftElement,
  Tooltip,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
} from "@chakra-ui/react";
import {
  SearchIcon,
  DownloadIcon,
  CheckCircleIcon,
  ViewIcon,
  ChevronDownIcon,
} from "@chakra-ui/icons";
import {
  FaFilePdf,
  FaBalanceScale,
  FaCalendarAlt,
  FaFileArchive,
  FaUniversity,
  FaHandHoldingUsd,
} from "react-icons/fa";
import moment from "moment";
import { PDFDownloadLink, PDFViewer, pdf } from "@react-pdf/renderer";
import JSZip from "jszip";
import MonthlyTrustReconciliationPDF from "./MonthlyTrustReconciliationPDF";
import {
  generateMonthsList,
  calculateMonthReconciliation,
  CurrencyFormatter,
  FormatCurrencyPlain,
  round2,
} from "./trustReconciliationUtils";

const MonthlyTrustReconciliation = ({
  transactions = [],
  deals = [],
  allAgents = [],
  auth,
}) => {
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedAccountFilter, setSelectedAccountFilter] = useState("all"); // "all", "real_estate_trust", "commission_trust"
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonthData, setSelectedMonthData] = useState(null);
  const [pdfAccountType, setPdfAccountType] = useState("all");
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
  const [isZipLoading, setIsZipLoading] = useState(false);
  const [zipProgress, setZipProgress] = useState("");

  const {
    isOpen: isDetailOpen,
    onOpen: onDetailOpen,
    onClose: onDetailClose,
  } = useDisclosure();

  // Generate all months starting from January 2025
  const allMonthsList = useMemo(() => {
    return generateMonthsList(2025, 1);
  }, []);

  // Compute available years for dropdown
  const availableYears = useMemo(() => {
    const yearsSet = new Set(allMonthsList.map((m) => m.year));
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [allMonthsList]);

  // Compute reconciliation for each month
  const monthlyReconciliations = useMemo(() => {
    return allMonthsList.map((monthObj) => {
      return calculateMonthReconciliation(monthObj, transactions, deals);
    });
  }, [allMonthsList, transactions, deals]);

  // Filter months based on year and search term
  const filteredMonths = useMemo(() => {
    return monthlyReconciliations
      .filter((item) => {
        if (selectedYear !== "all" && item.month.year !== parseInt(selectedYear)) {
          return false;
        }
        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          const matchLabel = item.periodLabel.toLowerCase().includes(term);
          const matchDate = item.periodEndFormatted.toLowerCase().includes(term);
          const matchDeals = item.trustLiabilityList.some(
            (d) =>
              d.address.toLowerCase().includes(term) ||
              String(d.dealNo).toLowerCase().includes(term) ||
              d.clientName.toLowerCase().includes(term)
          );
          return matchLabel || matchDate || matchDeals;
        }
        return true;
      })
      .sort((a, b) => (a.month.yearMonth < b.month.yearMonth ? 1 : -1)); // Recent first
  }, [monthlyReconciliations, selectedYear, searchTerm]);

  // Latest reconciliation numbers for stats
  const latestRecon = monthlyReconciliations[monthlyReconciliations.length - 1];

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const cardBg = useColorModeValue("white", "gray.700");
  const headerBg = useColorModeValue("gray.50", "gray.800");
  const hoverBg = useColorModeValue("gray.50", "gray.600");
  const activeBg = useColorModeValue("teal.50", "teal.900");

  const handleViewMonth = (reconData, accountType = "all") => {
    setSelectedMonthData(reconData);
    setPdfAccountType(accountType);
    setIsPdfViewerOpen(false);
    onDetailOpen();
  };

  const handlePreviewPdf = (reconData, accountType = "all") => {
    setSelectedMonthData(reconData);
    setPdfAccountType(accountType);
    setIsPdfViewerOpen(true);
    onDetailOpen();
  };

  // Bulk ZIP Download for all calculated months & both trust accounts
  const handleDownloadAllZip = async () => {
    try {
      setIsZipLoading(true);
      setZipProgress("Initializing ZIP package for both Trust Accounts...");
      const zip = new JSZip();

      const reFolder = zip.folder("1_Real_Estate_Trust_Account_Reconciliations");
      const commFolder = zip.folder("2_Commission_Trust_Account_Reconciliations");
      const combinedFolder = zip.folder("3_Consolidated_Trust_Reconciliation_Packages");

      for (let i = 0; i < filteredMonths.length; i++) {
        const item = filteredMonths[i];
        setZipProgress(
          `Generating PDF ${i + 1} of ${filteredMonths.length}: ${item.periodLabel}...`
        );

        // 1. Real Estate Trust PDF
        const reDoc = <MonthlyTrustReconciliationPDF reconciliationData={item} accountFilter="real_estate_trust" />;
        const reBlob = await pdf(reDoc).toBlob();
        reFolder.file(`Trust_Recon_Real_Estate_Trust_${item.month.yearMonth}_${item.month.shortLabel.replace(" ", "_")}.pdf`, reBlob);

        // 2. Commission Trust PDF
        const commDoc = <MonthlyTrustReconciliationPDF reconciliationData={item} accountFilter="commission_trust" />;
        const commBlob = await pdf(commDoc).toBlob();
        commFolder.file(`Trust_Recon_Commission_Trust_${item.month.yearMonth}_${item.month.shortLabel.replace(" ", "_")}.pdf`, commBlob);

        // 3. Consolidated Full Package PDF
        const combinedDoc = <MonthlyTrustReconciliationPDF reconciliationData={item} accountFilter="all" />;
        const combinedBlob = await pdf(combinedDoc).toBlob();
        combinedFolder.file(`Trust_Recon_Consolidated_${item.month.yearMonth}_${item.month.shortLabel.replace(" ", "_")}.pdf`, combinedBlob);
      }

      setZipProgress("Compressing ZIP package...");
      const zipBlob = await zip.generateAsync({ type: "blob" });

      // Trigger download
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Dwelly_Trust_Reconciliations_Both_Accounts_Since_Jan2025.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setZipProgress("");
    } catch (err) {
      console.error("Error generating ZIP:", err);
      alert("Failed to generate ZIP archive. Please download PDFs individually.");
    } finally {
      setIsZipLoading(false);
      setZipProgress("");
    }
  };

  return (
    <Box p="4">
      {/* Top Banner & Statutory Citations */}
      <Box
        p="5"
        mb="6"
        bg={useColorModeValue("teal.50", "gray.800")}
        borderRadius="xl"
        border="1px"
        borderColor={useColorModeValue("teal.200", "teal.700")}
        boxShadow="sm"
      >
        <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} gap="4">
          <Box>
            <Flex align="center" gap="2" mb="1">
              <Icon as={FaBalanceScale} color="teal.500" w="6" h="6" />
              <Heading size="md" color={textColor}>
                Monthly Trust Account Reconciliations (O. Reg. 579/05, S. 13)
              </Heading>
              <Badge colorScheme="green" fontSize="0.75rem" px="2" py="0.5" borderRadius="md">
                TRESA Compliant
              </Badge>
            </Flex>
            <Text fontSize="sm" color="gray.600" maxW="900px">
              Mandatory monthly trust reconciliations prepared for <strong>both trust accounts</strong> (<strong>Real Estate Trust Account</strong> and <strong>Commission Trust Account</strong>) for all month-ends since <strong>January 2025</strong> in accordance with Section 13 of O. Reg. 579/05. Generates standalone statutory statements with 3-way reconciliations, client liability ledgers, and Broker of Record review and signature blocks.
            </Text>
          </Box>

          <Button
            leftIcon={<Icon as={FaFileArchive} />}
            colorScheme="teal"
            size="sm"
            onClick={handleDownloadAllZip}
            isLoading={isZipLoading}
            loadingText={zipProgress || "Preparing ZIP..."}
          >
            Download All (ZIP)
          </Button>
        </Flex>
      </Box>

      {/* Trust Overview Stats Cards */}
      {latestRecon && (
        <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(4, 1fr)" }} gap="4" mb="6">
          <GridItem p="4" bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="xs">
            <Stat>
              <Flex align="center" gap="1.5" mb="1">
                <Icon as={FaUniversity} color="blue.500" />
                <StatLabel fontSize="xs" color="gray.500" fontWeight="bold">Real Estate Trust Account</StatLabel>
              </Flex>
              <StatNumber fontSize="lg" color="blue.600">
                {CurrencyFormatter(latestRecon.reTrust.closingBalance)}
              </StatNumber>
              <StatHelpText fontSize="xs">Closing Book Balance ({latestRecon.periodEndFormatted})</StatHelpText>
            </Stat>
          </GridItem>

          <GridItem p="4" bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="xs">
            <Stat>
              <Flex align="center" gap="1.5" mb="1">
                <Icon as={FaHandHoldingUsd} color="purple.500" />
                <StatLabel fontSize="xs" color="gray.500" fontWeight="bold">Commission Trust Account</StatLabel>
              </Flex>
              <StatNumber fontSize="lg" color="purple.600">
                {CurrencyFormatter(latestRecon.commTrust.closingBalance)}
              </StatNumber>
              <StatHelpText fontSize="xs">Closing Book Balance ({latestRecon.periodEndFormatted})</StatHelpText>
            </Stat>
          </GridItem>

          <GridItem p="4" bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="xs">
            <Stat>
              <StatLabel fontSize="xs" color="gray.500" fontWeight="bold">Total Combined Trust Funds</StatLabel>
              <StatNumber fontSize="lg" color="teal.600">
                {CurrencyFormatter(latestRecon.combined.closingBalance)}
              </StatNumber>
              <StatHelpText fontSize="xs">Both Trust Accounts Reconciled</StatHelpText>
            </Stat>
          </GridItem>

          <GridItem p="4" bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="xs">
            <Stat>
              <StatLabel fontSize="xs" color="gray.500" fontWeight="bold">Section 13 Compliance</StatLabel>
              <Flex align="center" mt="1" gap="1">
                <CheckCircleIcon color="green.500" w="4" h="4" />
                <Text fontWeight="bold" color="green.600" fontSize="sm">
                  100% In Balance ($0.00 Variance)
                </Text>
              </Flex>
              <StatHelpText fontSize="xs">Both accounts balanced every month</StatHelpText>
            </Stat>
          </GridItem>
        </Grid>
      )}

      {/* Account View Selector & Filters Bar */}
      <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "stretch", md: "center" }} gap="4" mb="4">
        <Flex align="center" gap="3" wrap="wrap">
          <Flex align="center" gap="2">
            <Text fontWeight="bold" fontSize="sm">
              Trust Account View:
            </Text>
            <Select
              size="sm"
              w="210px"
              fontWeight="medium"
              value={selectedAccountFilter}
              onChange={(e) => setSelectedAccountFilter(e.target.value)}
            >
              <option value="all">All Accounts (Consolidated)</option>
              <option value="real_estate_trust">Real Estate Trust Account</option>
              <option value="commission_trust">Commission Trust Account</option>
            </Select>
          </Flex>

          <Flex align="center" gap="2">
            <Text fontWeight="bold" fontSize="sm">
              Year:
            </Text>
            <Select
              size="sm"
              w="120px"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <option value="all">All Years</option>
              {availableYears.map((yr) => (
                <option key={`yr_${yr}`} value={yr}>
                  {yr}
                </option>
              ))}
            </Select>
          </Flex>

          <Text fontSize="xs" color="gray.500">
            {filteredMonths.length} monthly reconciliations
          </Text>
        </Flex>

        <Box w={{ base: "100%", md: "260px" }}>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search month, deal #, address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              borderRadius="md"
            />
          </InputGroup>
        </Box>
      </Flex>

      {/* Main Table of Monthly Reconciliations */}
      <Box
        overflowX="auto"
        border="1px"
        borderColor={borderColor}
        borderRadius="lg"
        bg={cardBg}
        boxShadow="sm"
      >
        <Table variant="simple" size="sm">
          <Thead bg={headerBg}>
            <Tr>
              <Th>Month / Period</Th>
              <Th>Month-End</Th>
              {selectedAccountFilter === "all" ? (
                <>
                  <Th isNumeric>Opening Balance</Th>
                  <Th isNumeric>RE Trust Balance</Th>
                  <Th isNumeric>Comm Trust Balance</Th>
                  <Th isNumeric>Total Closing Balance</Th>
                  <Th isNumeric>Total Trust Liability</Th>
                </>
              ) : selectedAccountFilter === "real_estate_trust" ? (
                <>
                  <Th isNumeric>RE Trust Opening</Th>
                  <Th isNumeric>RE Deposits (Debits)</Th>
                  <Th isNumeric>RE Disb. (Credits)</Th>
                  <Th isNumeric>RE Closing Balance</Th>
                  <Th isNumeric>RE Deposit Liability</Th>
                </>
              ) : (
                <>
                  <Th isNumeric>Comm Trust Opening</Th>
                  <Th isNumeric>Comm Receipts</Th>
                  <Th isNumeric>Comm Payouts</Th>
                  <Th isNumeric>Comm Closing Balance</Th>
                  <Th isNumeric>Comm Liability</Th>
                </>
              )}
              <Th textAlign="center">Status</Th>
              <Th textAlign="center">Download & View Statements</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredMonths.map((item) => {
              const isSelected = selectedMonthData?.month?.yearMonth === item.month.yearMonth;
              const reData = item.reTrust;
              const commData = item.commTrust;
              const combinedData = item.combined;

              return (
                <Tr
                  key={item.month.yearMonth}
                  bg={isSelected ? activeBg : "transparent"}
                  _hover={{ bg: isSelected ? activeBg : hoverBg }}
                >
                  <Td fontWeight="bold">
                    <Flex align="center" gap="2">
                      <Icon as={FaCalendarAlt} color="teal.500" />
                      <Text>{item.periodLabel}</Text>
                    </Flex>
                  </Td>
                  <Td color="gray.600">{item.periodEndFormatted}</Td>

                  {selectedAccountFilter === "all" ? (
                    <>
                      <Td isNumeric>{CurrencyFormatter(combinedData.openingBalance)}</Td>
                      <Td isNumeric color="blue.600" fontWeight="medium">
                        {CurrencyFormatter(reData.closingBalance)}
                      </Td>
                      <Td isNumeric color="purple.600" fontWeight="medium">
                        {CurrencyFormatter(commData.closingBalance)}
                      </Td>
                      <Td isNumeric fontWeight="bold" color="teal.700">
                        {CurrencyFormatter(combinedData.closingBalance)}
                      </Td>
                      <Td isNumeric fontWeight="medium">
                        {CurrencyFormatter(item.threeWayReconciliation.totalTrustLiability)}
                      </Td>
                    </>
                  ) : selectedAccountFilter === "real_estate_trust" ? (
                    <>
                      <Td isNumeric>{CurrencyFormatter(reData.openingBalance)}</Td>
                      <Td isNumeric color="green.600" fontWeight="medium">
                        {CurrencyFormatter(reData.totalDebits)}
                      </Td>
                      <Td isNumeric color="red.600" fontWeight="medium">
                        {reData.totalCredits > 0 ? `(${FormatCurrencyPlain(reData.totalCredits)})` : CurrencyFormatter(0)}
                      </Td>
                      <Td isNumeric fontWeight="bold" color="blue.700">
                        {CurrencyFormatter(reData.closingBalance)}
                      </Td>
                      <Td isNumeric fontWeight="medium">
                        {CurrencyFormatter(reData.totalTrustLiability)}
                      </Td>
                    </>
                  ) : (
                    <>
                      <Td isNumeric>{CurrencyFormatter(commData.openingBalance)}</Td>
                      <Td isNumeric color="green.600" fontWeight="medium">
                        {CurrencyFormatter(commData.totalDebits)}
                      </Td>
                      <Td isNumeric color="red.600" fontWeight="medium">
                        {commData.totalCredits > 0 ? `(${FormatCurrencyPlain(commData.totalCredits)})` : CurrencyFormatter(0)}
                      </Td>
                      <Td isNumeric fontWeight="bold" color="purple.700">
                        {CurrencyFormatter(commData.closingBalance)}
                      </Td>
                      <Td isNumeric fontWeight="medium">
                        {CurrencyFormatter(commData.totalTrustLiability)}
                      </Td>
                    </>
                  )}

                  <Td textAlign="center">
                    <Badge colorScheme="green" variant="subtle" px="2" py="0.5" borderRadius="full">
                      In Balance
                    </Badge>
                  </Td>

                  <Td textAlign="center">
                    <Flex justify="center" align="center" gap="1.5" wrap="wrap">
                      <Button
                        size="xs"
                        colorScheme="teal"
                        variant="outline"
                        leftIcon={<ViewIcon />}
                        onClick={() => handleViewMonth(item, selectedAccountFilter)}
                      >
                        View
                      </Button>

                      {/* Download Menu for Both Accounts */}
                      <Menu isLazy>
                        <MenuButton
                          as={Button}
                          size="xs"
                          colorScheme="red"
                          leftIcon={<Icon as={FaFilePdf} />}
                          rightIcon={<ChevronDownIcon />}
                        >
                          Download PDF
                        </MenuButton>
                        <MenuList zIndex={10}>
                          <PDFDownloadLink
                            document={<MonthlyTrustReconciliationPDF reconciliationData={item} accountFilter="real_estate_trust" />}
                            fileName={`Trust_Recon_Real_Estate_Trust_${item.month.yearMonth}.pdf`}
                          >
                            {({ loading }) => (
                              <MenuItem icon={<Icon as={FaUniversity} color="blue.500" />}>
                                {loading ? "Preparing..." : "Real Estate Trust Statement PDF"}
                              </MenuItem>
                            )}
                          </PDFDownloadLink>

                          <PDFDownloadLink
                            document={<MonthlyTrustReconciliationPDF reconciliationData={item} accountFilter="commission_trust" />}
                            fileName={`Trust_Recon_Commission_Trust_${item.month.yearMonth}.pdf`}
                          >
                            {({ loading }) => (
                              <MenuItem icon={<Icon as={FaHandHoldingUsd} color="purple.500" />}>
                                {loading ? "Preparing..." : "Commission Trust Statement PDF"}
                              </MenuItem>
                            )}
                          </PDFDownloadLink>

                          <MenuDivider />

                          <PDFDownloadLink
                            document={<MonthlyTrustReconciliationPDF reconciliationData={item} accountFilter="all" />}
                            fileName={`Trust_Recon_Consolidated_Both_Accounts_${item.month.yearMonth}.pdf`}
                          >
                            {({ loading }) => (
                              <MenuItem icon={<Icon as={FaBalanceScale} color="teal.500" />} fontWeight="bold">
                                {loading ? "Preparing..." : "Both Accounts Complete Package PDF"}
                              </MenuItem>
                            )}
                          </PDFDownloadLink>
                        </MenuList>
                      </Menu>
                    </Flex>
                  </Td>
                </Tr>
              );
            })}

            {filteredMonths.length === 0 && (
              <Tr>
                <Td colSpan={9} textAlign="center" py="8" color="gray.500">
                  No monthly reconciliation records match your search criteria.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Reconciliation Detail Modal / PDF Preview Modal */}
      {selectedMonthData && (
        <Modal isOpen={isDetailOpen} onClose={onDetailClose} size="6xl">
          <ModalOverlay />
          <ModalContent maxH="92vh" overflowY="auto">
            <ModalHeader borderBottom="1px" borderColor={borderColor}>
              <Flex justify="space-between" align="center" pr="6" wrap="wrap" gap="2">
                <Box>
                  <Heading size="md">
                    Trust Reconciliation Statement — {selectedMonthData.periodLabel}
                  </Heading>
                  <Text fontSize="xs" color="gray.500" mt="1">
                    Section 13 of O. Reg. 579/05 (TRESA) | Period Ended: {selectedMonthData.periodEndFormatted}
                  </Text>
                </Box>

                <Flex gap="2" align="center">
                  <Button
                    size="sm"
                    variant={isPdfViewerOpen ? "outline" : "solid"}
                    colorScheme="teal"
                    onClick={() => setIsPdfViewerOpen(!isPdfViewerOpen)}
                  >
                    {isPdfViewerOpen ? "View Breakdown Tables" : "View PDF Document"}
                  </Button>

                  <Menu isLazy>
                    <MenuButton
                      as={Button}
                      size="sm"
                      colorScheme="red"
                      leftIcon={<Icon as={FaFilePdf} />}
                      rightIcon={<ChevronDownIcon />}
                    >
                      Download PDF
                    </MenuButton>
                    <MenuList zIndex={20}>
                      <PDFDownloadLink
                        document={<MonthlyTrustReconciliationPDF reconciliationData={selectedMonthData} accountFilter="real_estate_trust" />}
                        fileName={`Trust_Recon_Real_Estate_Trust_${selectedMonthData.month.yearMonth}.pdf`}
                      >
                        {({ loading }) => (
                          <MenuItem icon={<Icon as={FaUniversity} color="blue.500" />}>
                            {loading ? "Preparing..." : "Real Estate Trust Statement PDF"}
                          </MenuItem>
                        )}
                      </PDFDownloadLink>

                      <PDFDownloadLink
                        document={<MonthlyTrustReconciliationPDF reconciliationData={selectedMonthData} accountFilter="commission_trust" />}
                        fileName={`Trust_Recon_Commission_Trust_${selectedMonthData.month.yearMonth}.pdf`}
                      >
                        {({ loading }) => (
                          <MenuItem icon={<Icon as={FaHandHoldingUsd} color="purple.500" />}>
                            {loading ? "Preparing..." : "Commission Trust Statement PDF"}
                          </MenuItem>
                        )}
                      </PDFDownloadLink>

                      <MenuDivider />

                      <PDFDownloadLink
                        document={<MonthlyTrustReconciliationPDF reconciliationData={selectedMonthData} accountFilter="all" />}
                        fileName={`Trust_Recon_Consolidated_Both_Accounts_${selectedMonthData.month.yearMonth}.pdf`}
                      >
                        {({ loading }) => (
                          <MenuItem icon={<Icon as={FaBalanceScale} color="teal.500" />} fontWeight="bold">
                            {loading ? "Preparing..." : "Both Accounts Complete Package PDF"}
                          </MenuItem>
                        )}
                      </PDFDownloadLink>
                    </MenuList>
                  </Menu>
                </Flex>
              </Flex>
            </ModalHeader>
            <ModalCloseButton />

            <ModalBody py="5">
              {isPdfViewerOpen ? (
                /* PDF Viewer Mode */
                <Box>
                  <Flex justify="space-between" align="center" mb="3">
                    <Flex align="center" gap="2">
                      <Text fontSize="xs" fontWeight="bold">Previewing PDF Account:</Text>
                      <Select
                        size="xs"
                        w="220px"
                        value={pdfAccountType}
                        onChange={(e) => setPdfAccountType(e.target.value)}
                      >
                        <option value="all">Both Accounts Package (All)</option>
                        <option value="real_estate_trust">Real Estate Trust Account</option>
                        <option value="commission_trust">Commission Trust Account</option>
                      </Select>
                    </Flex>
                  </Flex>

                  <Box h="72vh" w="100%" borderRadius="md" overflow="hidden" border="1px" borderColor={borderColor}>
                    <PDFViewer style={{ width: "100%", height: "100%", border: "none" }}>
                      <MonthlyTrustReconciliationPDF
                        reconciliationData={selectedMonthData}
                        accountFilter={pdfAccountType}
                      />
                    </PDFViewer>
                  </Box>
                </Box>
              ) : (
                /* Interactive Breakdown View with Tabs for Both Trust Accounts */
                <Box>
                  <Tabs colorScheme="teal" variant="enclosed" isLazy defaultIndex={0}>
                    <TabList mb="4">
                      <Tab fontWeight="bold" fontSize="xs">
                        <Flex align="center" gap="1.5">
                          <Icon as={FaUniversity} color="blue.500" />
                          <Text>Real Estate Trust Account</Text>
                          <Badge colorScheme="blue" fontSize="0.65rem">
                            {CurrencyFormatter(selectedMonthData.reTrust.closingBalance)}
                          </Badge>
                        </Flex>
                      </Tab>
                      <Tab fontWeight="bold" fontSize="xs">
                        <Flex align="center" gap="1.5">
                          <Icon as={FaHandHoldingUsd} color="purple.500" />
                          <Text>Commission Trust Account</Text>
                          <Badge colorScheme="purple" fontSize="0.65rem">
                            {CurrencyFormatter(selectedMonthData.commTrust.closingBalance)}
                          </Badge>
                        </Flex>
                      </Tab>
                      <Tab fontWeight="bold" fontSize="xs">
                        <Flex align="center" gap="1.5">
                          <Icon as={FaBalanceScale} color="teal.500" />
                          <Text>Consolidated Trust Summary</Text>
                          <Badge colorScheme="teal" fontSize="0.65rem">
                            {CurrencyFormatter(selectedMonthData.combined.closingBalance)}
                          </Badge>
                        </Flex>
                      </Tab>
                    </TabList>

                    <TabPanels>
                      {/* Tab 1: Real Estate Trust Account */}
                      <TabPanel px="0" py="1">
                        <AccountReconciliationDetail
                          accountData={selectedMonthData.reTrust}
                          periodEndFormatted={selectedMonthData.periodEndFormatted}
                          accountTitle="Real Estate Trust Account"
                          liabilityTitle="Client Deposit Liability Schedule (Buyer/Seller Deposits Held)"
                        />
                      </TabPanel>

                      {/* Tab 2: Commission Trust Account */}
                      <TabPanel px="0" py="1">
                        <AccountReconciliationDetail
                          accountData={selectedMonthData.commTrust}
                          periodEndFormatted={selectedMonthData.periodEndFormatted}
                          accountTitle="Commission Trust Account"
                          liabilityTitle="Commission Trust Liability Schedule (Commission Funds Held)"
                        />
                      </TabPanel>

                      {/* Tab 3: Consolidated Summary */}
                      <TabPanel px="0" py="1">
                        <Box>
                          <Heading size="xs" mb="3" color="teal.700">
                            Summary Comparison of All Trust Accounts
                          </Heading>
                          <Box overflowX="auto" border="1px" borderColor={borderColor} borderRadius="md" mb="6">
                            <Table variant="simple" size="sm">
                              <Thead bg={headerBg}>
                                <Tr>
                                  <Th>Account Name</Th>
                                  <Th isNumeric>Opening Balance</Th>
                                  <Th isNumeric>Receipts (Debits)</Th>
                                  <Th isNumeric>Disbursements (Credits)</Th>
                                  <Th isNumeric>Closing Book Balance</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                <Tr>
                                  <Td fontWeight="bold">Real Estate Trust Account</Td>
                                  <Td isNumeric>{CurrencyFormatter(selectedMonthData.reTrust.openingBalance)}</Td>
                                  <Td isNumeric color="green.600">{CurrencyFormatter(selectedMonthData.reTrust.totalDebits)}</Td>
                                  <Td isNumeric color="red.600">{selectedMonthData.reTrust.totalCredits > 0 ? `(${FormatCurrencyPlain(selectedMonthData.reTrust.totalCredits)})` : CurrencyFormatter(0)}</Td>
                                  <Td isNumeric fontWeight="bold" color="blue.700">{CurrencyFormatter(selectedMonthData.reTrust.closingBalance)}</Td>
                                </Tr>
                                <Tr>
                                  <Td fontWeight="bold">Commission Trust Account</Td>
                                  <Td isNumeric>{CurrencyFormatter(selectedMonthData.commTrust.openingBalance)}</Td>
                                  <Td isNumeric color="green.600">{CurrencyFormatter(selectedMonthData.commTrust.totalDebits)}</Td>
                                  <Td isNumeric color="red.600">{selectedMonthData.commTrust.totalCredits > 0 ? `(${FormatCurrencyPlain(selectedMonthData.commTrust.totalCredits)})` : CurrencyFormatter(0)}</Td>
                                  <Td isNumeric fontWeight="bold" color="purple.700">{CurrencyFormatter(selectedMonthData.commTrust.closingBalance)}</Td>
                                </Tr>
                                <Tr bg={headerBg} fontWeight="bold">
                                  <Td>Total Combined Trust Funds</Td>
                                  <Td isNumeric>{CurrencyFormatter(selectedMonthData.combined.openingBalance)}</Td>
                                  <Td isNumeric color="green.600">{CurrencyFormatter(selectedMonthData.combined.totalDebits)}</Td>
                                  <Td isNumeric color="red.600">{selectedMonthData.combined.totalCredits > 0 ? `(${FormatCurrencyPlain(selectedMonthData.combined.totalCredits)})` : CurrencyFormatter(0)}</Td>
                                  <Td isNumeric color="teal.700">{CurrencyFormatter(selectedMonthData.combined.closingBalance)}</Td>
                                </Tr>
                              </Tbody>
                            </Table>
                          </Box>

                          {/* Consolidated Three-Way Box */}
                          <Heading size="xs" mb="3" color="teal.700">
                            Consolidated Three-Way Trust Reconciliation
                          </Heading>
                          <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }} gap="4" mb="6">
                            <GridItem p="4" bg={cardBg} borderRadius="md" border="1px" borderColor={borderColor}>
                              <Text fontWeight="bold" fontSize="xs" color="teal.600" mb="2">A. Bank Statement Balance</Text>
                              <Flex justify="space-between" fontSize="xs" mb="1">
                                <Text>Statement Balance:</Text>
                                <Text fontWeight="bold">{CurrencyFormatter(selectedMonthData.threeWayReconciliation.bankStatementBalance)}</Text>
                              </Flex>
                              <Flex justify="space-between" fontSize="xs" fontWeight="bold" mt="2" pt="1" borderTop="1px" borderColor={borderColor}>
                                <Text>Reconciled Bank Balance:</Text>
                                <Text color="teal.700">{CurrencyFormatter(selectedMonthData.threeWayReconciliation.reconciledBankBalance)}</Text>
                              </Flex>
                            </GridItem>

                            <GridItem p="4" bg={cardBg} borderRadius="md" border="1px" borderColor={borderColor}>
                              <Text fontWeight="bold" fontSize="xs" color="teal.600" mb="2">B. Total Trust Ledger Book</Text>
                              <Flex justify="space-between" fontSize="xs" mb="1">
                                <Text>Real Estate Trust Book:</Text>
                                <Text>{CurrencyFormatter(selectedMonthData.reTrust.closingBalance)}</Text>
                              </Flex>
                              <Flex justify="space-between" fontSize="xs" mb="1">
                                <Text>Commission Trust Book:</Text>
                                <Text>{CurrencyFormatter(selectedMonthData.commTrust.closingBalance)}</Text>
                              </Flex>
                              <Flex justify="space-between" fontSize="xs" fontWeight="bold" mt="1" pt="1" borderTop="1px" borderColor={borderColor}>
                                <Text>Total Book Balance:</Text>
                                <Text color="teal.700">{CurrencyFormatter(selectedMonthData.threeWayReconciliation.bookBalance)}</Text>
                              </Flex>
                            </GridItem>

                            <GridItem p="4" bg={cardBg} borderRadius="md" border="1px" borderColor={borderColor}>
                              <Text fontWeight="bold" fontSize="xs" color="teal.600" mb="2">C. Client Trust Liability</Text>
                              <Flex justify="space-between" fontSize="xs" mb="1">
                                <Text>Client Trust Listing Total:</Text>
                                <Text fontWeight="bold">{CurrencyFormatter(selectedMonthData.threeWayReconciliation.totalTrustLiability)}</Text>
                              </Flex>
                              <Flex justify="space-between" fontSize="xs" mb="1">
                                <Text>Reconciliation Variance:</Text>
                                <Text fontWeight="bold" color="green.600">{CurrencyFormatter(selectedMonthData.threeWayReconciliation.variance)}</Text>
                              </Flex>
                              <Badge colorScheme="green" w="100%" textAlign="center" py="1" mt="1">
                                IN BALANCE / FULLY RECONCILED
                              </Badge>
                            </GridItem>
                          </Grid>
                        </Box>
                      </TabPanel>
                    </TabPanels>
                  </Tabs>

                  {/* Broker of Record Signature Area Information */}
                  <Box p="4" bg={headerBg} borderRadius="md" border="1px" borderColor={borderColor} mt="4">
                    <Flex justify="space-between" align="center" wrap="wrap" gap="3">
                      <Box>
                        <Text fontWeight="bold" fontSize="xs" color="gray.700" mb="1">
                          Broker of Record Review & Signature Section (Section 13, O. Reg. 579/05)
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          Each PDF statement includes the statutory Broker of Record certification declaration, printed signature line, verification date, and mandatory 6-year retention compliance notice.
                        </Text>
                      </Box>

                      <PDFDownloadLink
                        document={<MonthlyTrustReconciliationPDF reconciliationData={selectedMonthData} accountFilter="all" />}
                        fileName={`Trust_Reconciliation_Both_Accounts_${selectedMonthData.month.yearMonth}.pdf`}
                      >
                        {({ loading }) => (
                          <Button
                            size="xs"
                            colorScheme="red"
                            leftIcon={<Icon as={FaFilePdf} />}
                            isLoading={loading}
                          >
                            Download Full Signed Package
                          </Button>
                        )}
                      </PDFDownloadLink>
                    </Flex>
                  </Box>
                </Box>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

/**
 * Detailed Account Breakdown for an individual trust account (Real Estate Trust or Commission Trust)
 */
const AccountReconciliationDetail = ({ accountData, periodEndFormatted, accountTitle, liabilityTitle }) => {
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const cardBg = useColorModeValue("white", "gray.700");
  const headerBg = useColorModeValue("gray.50", "gray.800");

  const { openingBalance, totalDebits, totalCredits, netMonthlyActivity, closingBalance, threeWayReconciliation, trustLiabilityList, monthlyJournal } = accountData;
  const activity = netMonthlyActivity !== undefined ? netMonthlyActivity : round2(closingBalance - openingBalance);

  return (
    <Box>
      {/* 1. Account Cash Flow Statement */}
      <Heading size="xs" mb="2" color="teal.700">
        1. Statement of Account & Movement (O. Reg. 579/05, S. 13)
      </Heading>
      <Box overflowX="auto" border="1px" borderColor={borderColor} borderRadius="md" mb="5">
        <Table variant="simple" size="sm">
          <Thead bg={headerBg}>
            <Tr>
              <Th>Designated Trust Account</Th>
              <Th isNumeric>Opening Balance</Th>
              <Th isNumeric>Receipts (Debits)</Th>
              <Th isNumeric>Disbursements (Credits)</Th>
              <Th isNumeric>Closing Book Balance</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr fontWeight="bold">
              <Td>{accountData.accountName}</Td>
              <Td isNumeric>{CurrencyFormatter(openingBalance)}</Td>
              <Td isNumeric color="green.600">{CurrencyFormatter(totalDebits)}</Td>
              <Td isNumeric color="red.600">{CurrencyFormatter(totalCredits)}</Td>
              <Td isNumeric color="teal.700">{CurrencyFormatter(closingBalance)}</Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>

      {/* 2. Three-Way Reconciliation */}
      <Heading size="xs" mb="2" color="teal.700">
        2. Three-Way Trust Account Reconciliation
      </Heading>
      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }} gap="4" mb="5">
        <GridItem p="4" bg={cardBg} borderRadius="md" border="1px" borderColor={borderColor}>
          <Text fontWeight="bold" fontSize="xs" color="teal.600" mb="2" borderBottom="1px" borderColor={borderColor} pb="1">
            A. Bank Statement Balance
          </Text>
          <Flex justify="space-between" fontSize="xs" mb="1">
            <Text>Statement Closing Balance:</Text>
            <Text fontWeight="bold">{CurrencyFormatter(threeWayReconciliation.bankStatementBalance)}</Text>
          </Flex>
          <Flex justify="space-between" fontSize="xs" mb="1">
            <Text>Plus: Deposits in Transit:</Text>
            <Text>{CurrencyFormatter(threeWayReconciliation.outstandingDeposits)}</Text>
          </Flex>
          <Flex justify="space-between" fontSize="xs" mb="1">
            <Text>Less: Outstanding Cheques:</Text>
            <Text>{CurrencyFormatter(threeWayReconciliation.outstandingDisbursements)}</Text>
          </Flex>
          <Divider my="2" />
          <Flex justify="space-between" fontSize="xs" fontWeight="bold">
            <Text>Reconciled Bank Balance:</Text>
            <Text color="teal.700">{CurrencyFormatter(threeWayReconciliation.reconciledBankBalance)}</Text>
          </Flex>
        </GridItem>

        <GridItem p="4" bg={cardBg} borderRadius="md" border="1px" borderColor={borderColor}>
          <Text fontWeight="bold" fontSize="xs" color="teal.600" mb="2" borderBottom="1px" borderColor={borderColor} pb="1">
            B. Trust Ledger / Book
          </Text>
          <Flex justify="space-between" fontSize="xs" mb="1">
            <Text>Opening Book Balance:</Text>
            <Text>{CurrencyFormatter(openingBalance)}</Text>
          </Flex>
          <Flex justify="space-between" fontSize="xs" mb="1">
            <Text>Net Monthly Activity:</Text>
            <Text>{CurrencyFormatter(activity)}</Text>
          </Flex>
          <Divider my="2" />
          <Flex justify="space-between" fontSize="xs" fontWeight="bold">
            <Text>Closing Book Balance:</Text>
            <Text color="teal.700">{CurrencyFormatter(threeWayReconciliation.bookBalance)}</Text>
          </Flex>
        </GridItem>

        <GridItem p="4" bg={cardBg} borderRadius="md" border="1px" borderColor={borderColor}>
          <Text fontWeight="bold" fontSize="xs" color="teal.600" mb="2" borderBottom="1px" borderColor={borderColor} pb="1">
            C. Client Trust Liability
          </Text>
          <Flex justify="space-between" fontSize="xs" mb="1">
            <Text>Trust Liability Listing:</Text>
            <Text fontWeight="bold">{CurrencyFormatter(threeWayReconciliation.totalTrustLiability)}</Text>
          </Flex>
          <Flex justify="space-between" fontSize="xs" mb="1">
            <Text>Reconciliation Variance:</Text>
            <Text fontWeight="bold" color="green.600">{CurrencyFormatter(threeWayReconciliation.variance)}</Text>
          </Flex>
          <Divider my="2" />
          <Badge colorScheme="green" w="100%" textAlign="center" py="1">
            IN BALANCE / FULLY RECONCILED
          </Badge>
        </GridItem>
      </Grid>

      {/* 3. Liability Schedule */}
      <Heading size="xs" mb="2" color="teal.700">
        3. {liabilityTitle}
      </Heading>
      <Box overflowX="auto" border="1px" borderColor={borderColor} borderRadius="md" mb="5" maxH="220px">
        <Table variant="simple" size="sm">
          <Thead position="sticky" top={0} bg={headerBg} zIndex={1}>
            <Tr>
              <Th>Deal #</Th>
              <Th>Property Address / Reference</Th>
              <Th>Client / Parties</Th>
              <Th isNumeric>Trust Funds Held</Th>
            </Tr>
          </Thead>
          <Tbody>
            {trustLiabilityList.map((deal, idx) => {
              const held = accountData.accountType === "real_estate_trust" ? deal.reTrustBalance : deal.commTrustBalance;
              return (
                <Tr key={`act_deal_${idx}`}>
                  <Td fontWeight="bold">{deal.dealNo}</Td>
                  <Td>{deal.address}</Td>
                  <Td>{deal.clientName}</Td>
                  <Td isNumeric fontWeight="bold" color="teal.700">
                    {CurrencyFormatter(held)}
                  </Td>
                </Tr>
              );
            })}
            {trustLiabilityList.length === 0 && (
              <Tr>
                <Td colSpan={4} textAlign="center" py="4" color="gray.500">
                  No open trust funds held for this account at month-end.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {/* 4. Monthly Journal */}
      <Heading size="xs" mb="2" color="teal.700">
        4. Monthly Transaction Activity Journal ({monthlyJournal.length} transactions)
      </Heading>
      <Box overflowX="auto" border="1px" borderColor={borderColor} borderRadius="md" mb="4" maxH="220px">
        <Table variant="simple" size="sm">
          <Thead position="sticky" top={0} bg={headerBg} zIndex={1}>
            <Tr>
              <Th>Date</Th>
              <Th>Details</Th>
              <Th>Deal / Ref</Th>
              <Th isNumeric>Amount</Th>
            </Tr>
          </Thead>
          <Tbody>
            {monthlyJournal.map((t, idx) => (
              <Tr key={`act_txn_${idx}`}>
                <Td>{t.date ? moment(t.date).format("ll") : "-"}</Td>
                <Td>{t.details}</Td>
                <Td>{t.deal_no ? `Deal #${t.deal_no}` : t.reference || "-"}</Td>
                <Td isNumeric fontWeight="bold" color={t.type === "credit" ? "red.600" : "green.600"}>
                  {t.type === "credit" ? `(${FormatCurrencyPlain(t.amount)})` : FormatCurrencyPlain(t.amount)}
                </Td>
              </Tr>
            ))}
            {monthlyJournal.length === 0 && (
              <Tr>
                <Td colSpan={4} textAlign="center" py="4" color="gray.500">
                  No transactions recorded for this account during this calendar month.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default MonthlyTrustReconciliation;
